// Trigger sets — authored per report; see docs/builder-report-authoring-spec.md.
// Batch: workAtHeightRoofs (fragile roofs, gutter cleaning, fall arrest, edge protection).
import type { TriggerQuestion } from "./types";
import {
  ASBESTOS,
  OCCUPIED,
  OVERHEAD,
  FALLING_MATERIAL_PUBLIC,
  MANUAL_HANDLING,
  LADDER_INSTABILITY,
  FRAGILE_ROOF,
  ACCESS_OPTIONS,
  RESCUE_Q,
  PRE2000_Q,
} from "./shared";

// Task-specific hazard ids minted in this batch (kept in sync with the report JSONs).
const ROOFLIGHT = "haz-working-at-height-fall-through-rooflight-or-skylight";

export const WAH_ROOFS_TRIGGERS: Record<string, TriggerQuestion[]> = {
  "fragile-roof-work": [
    {
      key: "fragileConfirmed",
      label: "Is the whole roof being treated as fragile until a competent survey or ACR(M)001 proves otherwise?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [FRAGILE_ROOF], scopeLine: "The whole roof is treated as fragile until a competent person confirms otherwise by survey or ACR(M)001 evidence." },
        no: { failCheck: "Roof fragility assessed — assume fragile until a competent person confirms otherwise" },
      },
      notSure: { hazardsOn: [FRAGILE_ROOF], failCheck: "Roof fragility assessed — assume fragile until a competent person confirms otherwise" },
    },
    {
      key: "rooflights",
      label: "Are rooflights or skylights present on or near the work area?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [ROOFLIGHT] },
        no: { hazardsOff: [ROOFLIGHT], scopeLine: "No rooflights or skylights on or near the work area — confirmed on the roof survey." },
      },
      notSure: { hazardsOn: [ROOFLIGHT], failCheck: "Rooflight / skylight positions confirmed on the survey" },
    },
    PRE2000_Q([ASBESTOS], [ASBESTOS]),
    {
      key: "publicBelow",
      label: "Are occupants, the public or other trades below or nearby?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [OCCUPIED, FALLING_MATERIAL_PUBLIC], requireFields: ["nearbyTradesOrPublic"] },
        no: { hazardsOff: [OCCUPIED, FALLING_MATERIAL_PUBLIC], scopeLine: "No occupants, public or other trades below or adjacent to the work area during the works." },
      },
      notSure: { hazardsOn: [OCCUPIED, FALLING_MATERIAL_PUBLIC], failCheck: "Public / occupants / other trades below confirmed" },
    },
    {
      key: "overhead",
      label: "Are overhead power lines near the roof, MEWP or scaffold?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [OVERHEAD] },
        no: { hazardsOff: [OVERHEAD], scopeLine: "No overhead power lines within risk distance of the work area." },
      },
      notSure: { hazardsOn: [OVERHEAD], failCheck: "Overhead power line proximity confirmed" },
    },
    {
      key: "accessMethod",
      label: "How will the roof area be accessed?",
      kind: "choice",
      options: ["MEWP from below", "Independent scaffold", "Staging / crawling boards on the roof", "Safety netting beneath the work"],
      effects: {
        "MEWP from below": { scopeLine: "Access: MEWP from below to avoid going onto the roof surface where practicable." },
        "Independent scaffold": { scopeLine: "Access: independent scaffold with a guarded working platform, avoiding the roof surface where practicable." },
        "Staging / crawling boards on the roof": { scopeLine: "Access: staging / crawling boards spanning the load-bearing purlins where roof access is unavoidable." },
        "Safety netting beneath the work": { scopeLine: "Access: fall-arrest safety netting slung beneath the work area to catch a fall through the surface." },
      },
    },
    {
      key: "fallArrest",
      label: "Will personal fall arrest be used as a back-up where a fixed anchor and netting are provided?",
      kind: "yesno",
      effects: {
        yes: { scopeLine: "Personal fall arrest to a verified anchor is used as a back-up to collective protection where the latter is not reasonably practicable; a rescue plan is in place." },
        no: { scopeLine: "No personal fall arrest — fall from the roof edge is controlled by collective protection (perimeter edge protection, boards, netting); fall arrest is not relied on for this task." },
      },
    },
    RESCUE_Q,
  ],

  "gutter-cleaning": [
    {
      key: "accessMethod",
      label: "What access method will be used for each gutter run?",
      kind: "choice",
      options: ACCESS_OPTIONS,
      effects: Object.fromEntries(
        ACCESS_OPTIONS.map((o) => [o, { scopeLine: `Access for gutter clearance: ${o}.` }]),
      ),
    },
    {
      key: "ladderUse",
      label: "Will a ladder be used for any low, short, occasional runs?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [LADDER_INSTABILITY], scopeLine: "A ladder is used only for low, short, occasional runs where it is justified; it is tied and worked within the stiles." },
        no: { hazardsOff: [LADDER_INSTABILITY], scopeLine: "No ladder used — all runs are reached from a MEWP or mobile tower; ladder controls are not required for this task." },
      },
      notSure: { hazardsOn: [LADDER_INSTABILITY], failCheck: "Ladder use for low runs confirmed" },
    },
    {
      key: "biological",
      label: "Will gutter debris, silt or standing water be handled (bird droppings, decaying matter)?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: ["haz-coshh-coshh-hazardou-substance-exposure"], scopeLine: "Gutter debris, silt and standing water are treated as potentially contaminated (bird droppings, waterborne bacteria); COSHH controls apply." },
        no: {
          hazardsOff: ["haz-coshh-coshh-hazardou-substance-exposure"],
          substancesOff: ["Bird droppings"],
          emergencyOff: ["Biological exposure"],
          scopeLine: "No debris, silt or standing water handled (e.g. inspection only) — biological/COSHH controls are not required for this task.",
        },
      },
      notSure: { hazardsOn: ["haz-coshh-coshh-hazardou-substance-exposure"], failCheck: "Biological / contaminated debris exposure confirmed" },
    },
    {
      key: "publicBelow",
      label: "Are occupants, the public or other trades below or nearby?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [OCCUPIED, FALLING_MATERIAL_PUBLIC], requireFields: ["nearbyTradesOrPublic"] },
        no: { hazardsOff: [OCCUPIED, FALLING_MATERIAL_PUBLIC], scopeLine: "No occupants, public or other trades below or adjacent to the work area during the works." },
      },
      notSure: { hazardsOn: [OCCUPIED, FALLING_MATERIAL_PUBLIC], failCheck: "Public / occupants below confirmed" },
    },
    {
      key: "overhead",
      label: "Are overhead lines or service heads running near the eaves?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [OVERHEAD] },
        no: { hazardsOff: [OVERHEAD], scopeLine: "No overhead lines or service heads within risk distance of the eaves." },
      },
      notSure: { hazardsOn: [OVERHEAD], failCheck: "Overhead line / service head proximity confirmed" },
    },
    RESCUE_Q,
  ],

  "fall-arrest-harness": [
    {
      key: "collectiveJustification",
      label: "Why is collective protection (guardrail, net, platform) not reasonably practicable for this task?",
      kind: "text",
      placeholder: "e.g. isolated maintenance point on existing steelwork, no slab to fix a guardrail to",
      requiredCheck: "Justification recorded for using fall arrest instead of collective protection",
      effects: { "*": { scopeLine: "Collective protection is not reasonably practicable: {answer}. Personal fall arrest is used as the last line of the WAHR 2005 hierarchy." } },
    },
    {
      key: "anchorVerified",
      label: "Anchor point — who verified or designed it, and what is its rating?",
      kind: "text",
      placeholder: "e.g. eyebolt to EN 795 verified by structural engineer, sited above head height",
      requiredCheck: "Anchor verification / rating recorded",
      effects: { "*": { scopeLine: "Anchorage: {answer}." } },
    },
    {
      key: "clearance",
      label: "Has fall-factor and required clearance below the work been calculated and confirmed adequate?",
      kind: "yesno",
      effects: {
        yes: { scopeLine: "Fall-factor, lanyard deployment and total clearance below the work calculated and confirmed adequate before connecting." },
        no: { failCheck: "Fall-factor and clearance calculation confirmed" },
      },
      notSure: { failCheck: "Fall-factor and clearance calculation confirmed" },
    },
    {
      key: "examination",
      label: "Do the harness, lanyard and arrester carry an in-date LOLER thorough examination (within 6 months) plus a pre-use check?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: ["haz-working-at-height-defective-fall-arrest-equipment"], scopeLine: "Harness, lanyard and arrester carry an in-date 6-monthly LOLER thorough examination and a recorded pre-use check." },
        no: { hazardsOn: ["haz-working-at-height-defective-fall-arrest-equipment"], failCheck: "In-date LOLER thorough examination and pre-use check confirmed" },
      },
      notSure: { hazardsOn: ["haz-working-at-height-defective-fall-arrest-equipment"], failCheck: "In-date LOLER thorough examination and pre-use check confirmed" },
    },
    {
      key: "buddySystem",
      label: "Will a buddy system be in place so a fall is seen and the alarm raised immediately?",
      kind: "yesno",
      effects: {
        yes: { scopeLine: "Buddy system in place — a second trained person observes and can raise the alarm and start rescue at once." },
        no: { failCheck: "Buddy system for lone fall-arrest exposure confirmed" },
      },
      notSure: { failCheck: "Buddy system for lone fall-arrest exposure confirmed" },
    },
    RESCUE_Q,
  ],

  "edge-protection": [
    {
      key: "systemClass",
      label: "Which EN 13374 class of system suits the edge?",
      kind: "choice",
      options: ["Class A — near-flat surface (up to ~10 degrees)", "Class B — steeper surface / lower-energy fall", "Class C — steep or pitched surface (arrests a sliding person)"],
      effects: {
        "Class A — near-flat surface (up to ~10 degrees)": { scopeLine: "Edge protection: EN 13374 Class A for a near-flat surface." },
        "Class B — steeper surface / lower-energy fall": { scopeLine: "Edge protection: EN 13374 Class B for a steeper surface / lower-energy fall." },
        "Class C — steep or pitched surface (arrests a sliding person)": { scopeLine: "Edge protection: EN 13374 Class C for a steep or pitched surface that must arrest a sliding person." },
      },
    },
    {
      key: "firstRunMethod",
      label: "How is the installer protected on the first run before any guardrail exists?",
      kind: "choice",
      options: ["Install from the protected side toward the edge", "MEWP / podium / from below", "Fall arrest to a verified anchor"],
      effects: {
        "Install from the protected side toward the edge": { scopeLine: "First run installed from the protected side working toward the open edge." },
        "MEWP / podium / from below": { scopeLine: "First run installed from a MEWP, podium or from below, avoiding the open edge." },
        "Fall arrest to a verified anchor": { scopeLine: "First run installed with personal fall arrest to a verified anchor while exposed to the open edge." },
      },
    },
    {
      key: "publicBelow",
      label: "Are occupants, the public or other trades below or nearby?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [FALLING_MATERIAL_PUBLIC], requireFields: ["nearbyTradesOrPublic"] },
        no: { hazardsOff: [FALLING_MATERIAL_PUBLIC], scopeLine: "No occupants, public or other trades below or adjacent to the edge during installation." },
      },
      notSure: { hazardsOn: [FALLING_MATERIAL_PUBLIC], failCheck: "Public / other trades below confirmed" },
    },
    {
      key: "overhead",
      label: "Are overhead power lines near the edge, MEWP or podium?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [OVERHEAD] },
        no: { hazardsOff: [OVERHEAD], scopeLine: "No overhead power lines within risk distance of the work area." },
      },
      notSure: { hazardsOn: [OVERHEAD], failCheck: "Overhead power line proximity confirmed" },
    },
    {
      key: "manualHandling",
      label: "Will heavy counterweight bases or bundles of posts and rails be handled by hand?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [MANUAL_HANDLING] },
        no: { hazardsOff: [MANUAL_HANDLING], scopeLine: "Components are mechanically handled / craned to position — manual handling controls are not required for this task." },
      },
      notSure: { hazardsOn: [MANUAL_HANDLING], failCheck: "Manual handling of components confirmed" },
    },
    {
      key: "handover",
      label: "Will the completed run be inspected, tagged and handed over before trades rely on it?",
      kind: "yesno",
      effects: {
        yes: { scopeLine: "Completed edge protection inspected for rail continuity, gaps and fixings, tagged complete and handed over before following trades rely on it." },
        no: { failCheck: "Handover inspection and tagging arranged" },
      },
      notSure: { failCheck: "Handover inspection and tagging arranged" },
    },
    RESCUE_Q,
  ],
};
